import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Header from "components/Header/Header.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import Footer from "components/Footer/Footer.js";
import API from "utils/api";
import LangToggle from "views/Product/LangToggle.js";
import translations from "./translations.js";
import styles from "./invalidCertStyle.js";

const useStyles = makeStyles(styles);

/**
 * Where a scan that does not resolve to a certified product lands.
 *
 * Every sentence on this page is a claim about a licensee's certification, so
 * the page renders exactly one of four states and each says only what the
 * server was actually able to confirm:
 *
 *   checking     nothing established yet — no claim
 *   invalid      the server reported certValid=false — the only state that
 *                may advise against a purchase
 *   notFound     a 404: no product carries this identifier
 *   unconfirmed  the check could not be made — no claim
 */
export default function InvalidCertPage(props) {
  const classes = useStyles();
  const { location, history } = props;

  const params = new URLSearchParams(location.search);
  const authId = params.get("authId");
  // Set only on the 404 branch (ProductPage.js): "no product carries this
  // authId" is not the same claim as "certification invalid" — it is equally
  // produced by a damaged QR code, a typo, a deleted product, or an API that
  // is not deployed yet. This page must not assert more than a 404 supports.
  const isUnknown = params.get("reason") === "unknown";

  // ProductPage hands over what it already read. When it does, this page has
  // nothing to verify and nothing to fetch — which is what stops one failed
  // scan writing two rows into the certifier's scan log, since the endpoint
  // records a scan for every call it receives.
  const scan =
    location.state && location.state.verifiedByScan === true
      ? location.state
      : null;

  const [lang, setLang] = useState("hu");
  const [status, setStatus] = useState(function () {
    // With no identifier there is no product to say anything about, so the
    // page must not default to the accusation.
    if (!authId) return "missingId";
    if (scan) return isUnknown ? "notFound" : "invalid";
    return "checking";
  });
  const [productName, setProductName] = useState(scan ? scan.productName : null);
  const t = translations[lang];

  useEffect(() => {
    // Nothing to check: either the scan already told us, or there is no id.
    if (scan || !authId) return undefined;

    let cancelled = false;

    API.get("/product/ultragreen/" + encodeURIComponent(authId))
      .then(function (response) {
        if (cancelled) return;
        // The page used to read only the name and state the accusation
        // regardless. Anyone could therefore put a currently certified
        // product's id in this URL and have the certifier's own domain tell
        // readers not to buy it. The certification status is right there in
        // the response — honour it.
        if (response.data && response.data.certValid === true) {
          history.replace("/p/" + encodeURIComponent(authId));
          return;
        }
        setProductName(
          response.data && response.data.name ? response.data.name : null
        );
        setStatus("invalid");
      })
      .catch(function (error) {
        if (cancelled) return;
        // A 404 is the one failure that does say something: no product carries
        // this id. Anything else — network, 5xx — means the check did not
        // happen, and an unverified accusation is what this page must never
        // make.
        const notFound = error.response && error.response.status === 404;
        setProductName(null);
        setStatus(notFound ? "notFound" : "unconfirmed");
      });

    return function () {
      cancelled = true;
    };
  }, [authId, scan, isUnknown, history]);

  // `status` is the only thing that decides what is said. The `reason` query
  // parameter seeds it for a redirect from ProductPage, but on a direct visit
  // the API's fresh answer outranks whatever the URL happens to carry — a
  // stale `reason=unknown` must not talk over a server that just said the
  // product exists.
  //
  // Only a verified invalid certification wears the correction-ink accent.
  const alarm = status === "invalid";

  const copy = {
    checking: [t.checkingEyebrow, t.checkingTitle, t.checkingBody],
    invalid: [t.invalidEyebrow, t.invalidTitle, t.invalidBody],
    notFound: [t.notFoundEyebrow, t.notFoundTitle, t.notFoundBody],
    unconfirmed: [t.unconfirmedEyebrow, t.unconfirmedTitle, t.unconfirmedBody],
    missingId: [t.missingIdEyebrow, t.missingIdTitle, t.missingIdBody],
  }[status];

  const toggleLang = () => setLang((prev) => (prev === "hu" ? "en" : "hu"));

  return (
    <div className={classes.page}>
      <Header
        color="white"
        brand="UltraGreen"
        rightLinks={<HeaderLinks />}
        fixed
      />

      <div className={classes.main}>
        <div className={classes.band}>
          <div className={classes.inner}>
            <div className={classes.langRow}>
              <LangToggle t={t} onToggle={toggleLang} />
            </div>

            <div className={classes.statement}>
              <div className={classes.eyebrow}>
                <span
                  className={`${classes.eyebrowRule}${
                    alarm ? " " + classes.eyebrowRuleAlarm : ""
                  }`}
                />
                <span
                  className={`${classes.eyebrowText}${
                    alarm ? " " + classes.eyebrowTextAlarm : ""
                  }`}
                >
                  {copy[0]}
                </span>
              </div>

              <h1 className={classes.title}>{copy[1]}</h1>
              <p className={classes.body}>{copy[2]}</p>

              {authId ? (
                <div className={classes.identifier}>
                  <span className={classes.identifierLabel}>
                    {productName ? t.labelProductName : t.labelScannedId}
                  </span>
                  {productName ? (
                    <p className={classes.identifierName}>{productName}</p>
                  ) : (
                    <p className={classes.identifierValue}>{authId}</p>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className={classes.next}>
          <div className={classes.band}>
            <div className={classes.inner}>
              <div className={classes.nextInner}>
                <h2 className={classes.nextTitle}>{t.nextTitle}</h2>
                <div className={classes.cards}>
                  <div className={classes.card}>
                    <p className={classes.cardTitle}>{t.searchTitle}</p>
                    <p className={classes.cardBody}>{t.searchBody}</p>
                    <Link to="/claim-check" className={classes.cardAction}>
                      {t.searchAction}
                    </Link>
                  </div>
                  <div className={classes.card}>
                    <p className={classes.cardTitle}>{t.contactTitle}</p>
                    <p className={classes.cardBody}>{t.contactBody}</p>
                    <a
                      className={classes.cardEmail}
                      href={"mailto:" + t.contactEmail}
                    >
                      {t.contactEmail}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={classes.band}>
        <div className={classes.inner}>
          <p className={classes.companyLine}>{t.footerCompany}</p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
